import { useState, useEffect } from 'react';
import { AppLayout } from '../../components/layout';
import { Button, Card, EmptyState, Alert, MetricCard, StatusBadge } from '../../components/core';
import { http } from '../../lib/api';
import { useUiStore } from '../../store/ui';
import { loadCheckout } from '../../lib/razorpay';
import type { PlanDto, RazorpayOrderResponseDto } from '@livestreamx/shared-types';
import { priceForCycle, planCycleOf } from '@livestreamx/shared-types';
import { NAV } from './nav';

interface SubscriptionDto {
  id: string;
  status: string;
  /** Primary (oldest entitled) plan — null when a row exists with no plan attached. */
  plan: PlanDto | null;
  billingCycle: string | null;
  startAt: string | null;
  endAt: string | null;
  nextBillingAt: string | null;
  /** Every active plan this tenant holds. */
  plans: {
    id: string; planId: string; name: string; status: string;
    billingCycle: string; startAt: string | null; endAt: string | null;
  }[];
  /** Sum of all active plans — the limits enforced server-side. */
  limits: {
    maxStreams: number;
    maxDestinationsPerStream: number;
    maxTotalDestinations: number;
    maxStorageGb: number;
    maxBandwidthGb: number;
    recordingEnabled: boolean;
    maxInboundBitrateMbps: number;
    maxOutboundBitrateMbps: number;
    viewLimit: number | null;
  };
}

/** Format price inline in INR */
const fmtP = (n: number) => n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 });

/** Cycle suffix: "/day", "/mo", "/yr" */
const CYCLE_SUFFIX: Record<string, string> = { DAILY: '/day', MONTHLY: '/mo', YEARLY: '/yr' };

export function BillingPage() {
  const ui = useUiStore();
  const [subscription, setSubscription] = useState<SubscriptionDto | null>(null);
  const [plans, setPlans] = useState<PlanDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [gateSaving, setGateSaving] = useState(false);

  useEffect(() => {
    void http.get('/api/billing/subscription').then(setSubscription).catch(() => {});
    void http.get<PlanDto[]>('/api/plans').then(setPlans).catch(() => {});
    void http.get<{ billingEnabled?: boolean }>('/api/tenant/settings')
      .then((d) => { if (typeof d.billingEnabled === 'boolean') setBillingEnabled(d.billingEnabled); })
      .catch(() => {});
    setTimeout(() => setLoading(false), 500);
  }, []);

  const toggleGate = async (next: boolean) => {
    setGateSaving(true);
    try {
      await http.patch('/api/tenant/settings', { billingEnabled: next });
      setBillingEnabled(next);
      ui.toast('success', `Payment gateway ${next ? 'enabled' : 'disabled'}`);
    } catch (e) {
      ui.toast('danger', 'Failed to update gateway', (e as Error).message ?? 'Unknown error');
    } finally {
      setGateSaving(false);
    }
  };

  const subscribe = async (planId: string, planName: string) => {
    setActionLoading(planId);
    try {
      const order = await http.post<RazorpayOrderResponseDto>('/api/billing/orders', {
        planId,
        billingCycle: 'MONTHLY',
      });
      const Razorpay = await loadCheckout();
      const handle = new Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'LiveStreamX',
        description: planName,
        order_id: order.orderId,
        handler: (res) => {
          ui.toast('success', 'Payment submitted', `Ref ${res.razorpay_payment_id} — activating subscription (server verifies via webhook)`);
          setTimeout(() => void http.get('/api/billing/subscription').then(setSubscription).catch(() => {}), 6000);
        },
        modal: { ondismiss: () => setActionLoading(null) },
      });
      handle.open();
    } catch (e) {
      const msg = (e as Error).message ?? '';
      ui.toast('danger', msg.includes('not configured') ? 'Billing not configured' : 'Payment error', msg);
      setActionLoading(null);
    }
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="billing-page">
        <h1 className="page-title">Billing</h1>
        {error && <Alert kind="danger">{error}</Alert>}
        {loading ? <div className="mt-4 text-muted">Loading billing…</div> : (
          <>
            <Card title="Payment gateway" className="mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted" style={{ margin: 0 }}>
                    Razorpay checkout is {billingEnabled ? 'enabled' : 'disabled'} for this workspace.
                  </p>
                  {!billingEnabled && (
                    <p className="text-sm text-muted" style={{ margin: '4px 0 0' }}>
                      Plans below cannot be purchased until you enable it.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button className={`btn ${billingEnabled ? 'secondary' : 'ghost'} sm`} disabled={gateSaving} onClick={() => toggleGate(true)}>Enabled</button>
                  <button className={`btn ${!billingEnabled ? 'secondary' : 'ghost'} sm`} disabled={gateSaving} onClick={() => toggleGate(false)}>Disabled</button>
                </div>
              </div>
            </Card>
            {subscription ? (
              <Card title="Current subscription" className="mt-3">
                <div className="grid gap-2">
                  <div>
                    <span className="text-muted text-sm">
                      {subscription.plans.length > 1 ? `Plans (${subscription.plans.length})` : 'Plan'}
                    </span>
                    <div className="font-medium">
                      {subscription.plans.map((p) => p.name).join(' + ') ||
                        subscription.plan?.name ||
                        'No plan'}
                    </div>
                  </div>
                  <div><span className="text-muted text-sm">Status</span><div className="font-medium">{subscription.status}</div></div>
                  <div><span className="text-muted text-sm">Billing cycle</span><div className="font-medium">{subscription.billingCycle?.toLowerCase()}</div></div>
                  <div><span className="text-muted text-sm">Next billing</span><div className="font-medium">{subscription.nextBillingAt ? new Date(subscription.nextBillingAt).toLocaleDateString() : '—'}</div></div>
                </div>
                <hr className="mt-3" />
                <div className="grid gap-2 mt-3">
                  <span className="text-muted text-sm">Your effective limits (summed across active plans)</span>
                  <div className="font-medium">
                    {subscription.limits.maxStreams} streams ·{' '}
                    {subscription.limits.maxDestinationsPerStream || 'unlimited'} destinations per stream ·{' '}
                    {subscription.limits.maxTotalDestinations || 'unlimited'} destinations total
                  </div>
                  <div className="text-sm text-muted">
                    Recording {subscription.limits.recordingEnabled ? 'included' : 'not included'}
                    {subscription.limits.maxInboundBitrateMbps
                      ? ` · ingest up to ${subscription.limits.maxInboundBitrateMbps} Mbps`
                      : ''}
                    {subscription.limits.maxOutboundBitrateMbps
                      ? ` · up to ${subscription.limits.maxOutboundBitrateMbps} Mbps out per destination`
                      : ''}
                  </div>
                  {subscription.limits.maxInboundBitrateMbps != null && (
                    <div className="plan-limit"><span className="plan-limit-label">In</span><span className="plan-limit-value">{subscription.limits.maxInboundBitrateMbps || '∞'} Mbps</span></div>
                  )}
                  {subscription.limits.maxOutboundBitrateMbps != null && (
                    <div className="plan-limit"><span className="plan-limit-label">Out/Dest</span><span className="plan-limit-value">{subscription.limits.maxOutboundBitrateMbps || '∞'} Mbps</span></div>
                  )}
                  {subscription.limits.viewLimit != null && (
                    <div className="plan-limit"><span className="plan-limit-label">Viewers</span><span className="plan-limit-value">{subscription.limits.viewLimit || '∞'}</span></div>
                  )}
                </div>
              </Card>
            ) : (
              <EmptyState title="No active subscription" body="Choose a plan to unlock all features." />
            )}

            <Card title="Available plans" className="mt-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {plans.map((p) => (
                  <div key={p.id} className="plan-card">
                    <h3 className="font-semibold">{p.name}</h3>
                    <StatusBadge status={p.status} />
                    <div className="text-2xl font-bold">{fmtP(priceForCycle(p, planCycleOf(p)))}{CYCLE_SUFFIX[planCycleOf(p)]}</div>
                    <p className="text-muted text-sm mt-2">{p.description || ''}</p>
                    <ul className="text-sm text-muted mt-2 space-y-1">
                      <li>{p.maxStreams} streams</li>
                      <li>{p.maxDestinationsPerStream} destinations/stream</li>
                      <li>{p.maxTotalDestinations} total destinations</li>
                    </ul>
                    {p.maxStorageGb != null && <p className="text-sm text-muted">Storage: {p.maxStorageGb} GB</p>}
                    {p.maxBandwidthGb != null && <p className="text-sm text-muted">Bandwidth: {p.maxBandwidthGb} GB</p>}
                    {p.maxInboundBitrateMbps != null && <p className="text-sm text-muted">In: {p.maxInboundBitrateMbps} Mbps</p>}
                    {p.maxOutboundBitrateMbps != null && <p className="text-sm text-muted">Out/Dest: {p.maxOutboundBitrateMbps} Mbps</p>}
                    {p.recordingEnabled && <p className="text-sm text-muted mt-1"><strong>Recording: ✓</strong></p>}
                     {p.viewLimit != null && <p className="text-sm text-muted"><strong>Viewers: {p.viewLimit}</strong></p>}
                    <Button className="mt-2 w-full" size="sm" onClick={() => subscribe(p.id, p.name)} disabled={!!actionLoading || !billingEnabled}>
                      {!billingEnabled ? 'Gateway disabled' : actionLoading === p.id ? 'Processing…' : 'Subscribe'}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  );
}
